import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/system';
import KpiCard from './KpiCard';
import { deactivateValet, reactivateValet } from '../../services/adminService';

const SVC_COLORS = ['#7F77DD', '#1D9E75', '#378ADD', '#D4537E'];

const SectionHead = styled(Typography)({
  fontSize: '11px',
  fontWeight: 500,
  color: '#9b9b97',
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
  fontFamily: '"SF Mono", "Fira Code", monospace',
  marginBottom: '10px',
});

const Grid4 = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '10px',
  marginBottom: '14px',
});

const TableWrapper = styled(Box)({
  backgroundColor: '#ffffff',
  border: '0.5px solid #e0ddd6',
  borderRadius: '12px',
  overflow: 'hidden',
});

const StyledHeaderCell = styled(TableCell)({
  fontSize: '10px',
  color: '#9b9b97',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  fontFamily: '"SF Mono", "Fira Code", monospace',
  fontWeight: 500,
  padding: '7px 10px',
  borderBottom: '0.5px solid #e0ddd6',
  backgroundColor: '#f4f3ef',
});

const StyledCell = styled(TableCell)({
  padding: '10px 10px',
  fontSize: '12px',
  borderBottom: '0.5px solid #e0ddd6',
});

const MonoCell = styled(StyledCell)({
  fontFamily: '"SF Mono", "Fira Code", monospace',
});

const stickyLeftHeader = {
  position: 'sticky',
  left: 0,
  zIndex: 3,
  backgroundColor: '#f4f3ef',
  boxShadow: '1px 0 0 0 #e0ddd6',
};
const stickyRightHeader = {
  position: 'sticky',
  right: 0,
  zIndex: 3,
  backgroundColor: '#f4f3ef',
  boxShadow: '-1px 0 0 0 #e0ddd6',
};
const stickyLeftCell = {
  position: 'sticky',
  left: 0,
  zIndex: 1,
  backgroundColor: '#ffffff',
  boxShadow: '1px 0 0 0 #e0ddd6',
  'tr:hover &': { backgroundColor: '#f4f3ef' },
};
const stickyRightCell = {
  position: 'sticky',
  right: 0,
  zIndex: 1,
  backgroundColor: '#ffffff',
  boxShadow: '-1px 0 0 0 #e0ddd6',
  'tr:hover &': { backgroundColor: '#f4f3ef' },
};

const StatusPill = styled(Box)(({ active }) => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '10px',
  fontSize: '10px',
  fontFamily: '"SF Mono", "Fira Code", monospace',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  backgroundColor: active ? '#E1F5EE' : '#f4e4e4',
  color: active ? '#1D9E75' : '#993C1D',
}));

const StatusChip = styled(Box)(({ selected }) => ({
  display: 'inline-block',
  padding: '3px 10px',
  borderRadius: '10px',
  fontSize: '11px',
  cursor: 'pointer',
  border: '0.5px solid',
  borderColor: selected ? '#1a1a1a' : '#e0ddd6',
  backgroundColor: selected ? '#1a1a1a' : '#ffffff',
  color: selected ? '#ffffff' : '#6b6b68',
  fontFamily: '"SF Mono", "Fira Code", monospace',
  userSelect: 'none',
}));

const Divider = styled(Box)({
  height: '0.5px',
  backgroundColor: '#e0ddd6',
  margin: '24px 0',
});

function getAcceptColor(rate) {
  const val = parseFloat(rate);
  if (val >= 95) return '#3B6D11';
  if (val >= 90) return '#854F0B';
  return '#993C1D';
}

function getMissedColor(rate) {
  const val = parseFloat(rate);
  if (val <= 5) return '#3B6D11';
  if (val <= 10) return '#854F0B';
  return '#993C1D';
}

function fmtK(n) {
  return n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n);
}

// ── Roster helpers ────────────────────────────────────────────────────────────

function formatDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function isActive(v) {
  if (typeof v.active === 'boolean') return v.active;
  if (typeof v.isActive === 'boolean') return v.isActive;
  if (v.status) return String(v.status).toLowerCase() === 'active';
  return true;
}

function rowId(v) {
  return v._id || v.id || v.valetId;
}

function extractValets(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.valets)) return data.valets;
  if (Array.isArray(data.data)) return data.data;
  return [];
}

function fullName(v) {
  if (v.firstName || v.lastName) return `${v.firstName || ''} ${v.lastName || ''}`.trim();
  return v.name || v.fullName || v.valetName || '';
}

function matchesSearch(v, q) {
  if (!q) return true;
  const lower = q.toLowerCase();
  return (
    fullName(v).toLowerCase().includes(lower) ||
    (v.phone || v.phoneNumber || '').toLowerCase().includes(lower) ||
    (v.email || '').toLowerCase().includes(lower)
  );
}

// ── Performance helpers ───────────────────────────────────────────────────────

function mergeValetData(data) {
  const arrays = [
    { key: 'jobsPerValet', field: 'completed' },
    { key: 'jobsPerValetHour', field: 'jobsPerHour' },
    { key: 'dispatchAcceptTime', field: 'dispatchAccept' },
    { key: 'acceptanceRate', field: 'acceptRate' },
    { key: 'missedRequests', field: 'missedRate' },
    { key: 'pickupDistance', field: 'pickupDist' },
    { key: 'parkingSearchTime', field: 'parkSearch' },
    { key: 'retrievalTime', field: 'retrieval' },
    { key: 'idleTime', field: 'idleTime' },
    { key: 'aspMovesPerHour', field: 'aspPerHour' },
  ];

  const valetMap = {};

  for (const { key, field } of arrays) {
    const arr = data[key];
    if (!Array.isArray(arr)) continue;
    arr.forEach((item) => {
      const id = item.valetId || item.id;
      if (!id) return;
      if (!valetMap[id]) {
        valetMap[id] = { valetId: id, valetName: item.valetName || item.name || '' };
      }
      valetMap[id][field] = item.value ?? item[field] ?? null;
      if (item.valetName || item.name) {
        valetMap[id].valetName = item.valetName || item.name;
      }
    });
  }

  return Object.values(valetMap);
}

// ── Roster section (stateful) ─────────────────────────────────────────────────

function RosterSection({ rosterData, rosterError }) {
  const [rows, setRows] = useState(() => extractValets(rosterData));
  const [pending, setPending] = useState({});
  const [toast, setToast] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  React.useEffect(() => {
    setRows(extractValets(rosterData));
  }, [rosterData]);

  const handleActionClick = (valet) => {
    setConfirmTarget({ valet, action: isActive(valet) ? 'deactivate' : 'reactivate' });
  };

  const performAction = async ({ valet, action }) => {
    const id = rowId(valet);
    if (!id) return;

    const nextActive = action === 'reactivate';
    const apiCall = action === 'reactivate' ? reactivateValet : deactivateValet;
    const pastTense = action === 'reactivate' ? 'reactivated' : 'deactivated';

    setRows((prev) => prev.map((r) => rowId(r) === id ? { ...r, isActive: nextActive, active: nextActive } : r));
    setPending((p) => ({ ...p, [id]: true }));

    try {
      await apiCall(id);
      setToast({ severity: 'success', msg: `${fullName(valet) || 'Valet'} ${pastTense}.` });
    } catch (err) {
      setRows((prev) => prev.map((r) => rowId(r) === id ? valet : r));
      setToast({ severity: 'error', msg: err.message || 'Action failed' });
    } finally {
      setPending((p) => { const n = { ...p }; delete n[id]; return n; });
    }
  };

  const handleConfirmAction = () => {
    const target = confirmTarget;
    setConfirmTarget(null);
    performAction(target);
  };

  if (rosterError) {
    return (
      <Typography sx={{ color: '#993C1D', fontSize: '13px', textAlign: 'center', padding: '20px' }}>
        {rosterError}
      </Typography>
    );
  }

  if (rosterData === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
        <CircularProgress size={24} sx={{ color: '#1a1a1a' }} />
      </Box>
    );
  }

  const filtered = rows.filter((v) => {
    if (statusFilter === 'active' && !isActive(v)) return false;
    if (statusFilter === 'inactive' && isActive(v)) return false;
    return matchesSearch(v, search);
  });

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '12px', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            flex: '1 1 220px',
            maxWidth: 320,
            '& .MuiOutlinedInput-root': { fontSize: '12px', borderRadius: '8px' },
          }}
        />
        <Box sx={{ display: 'flex', gap: '6px' }}>
          {['all', 'active', 'inactive'].map((f) => (
            <StatusChip key={f} selected={statusFilter === f} onClick={() => setStatusFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </StatusChip>
          ))}
        </Box>
      </Box>

      <SectionHead>
        Valet roster ({filtered.length}{filtered.length !== rows.length ? ` of ${rows.length}` : ''})
      </SectionHead>

      <TableWrapper>
        <Box sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1500 }}>
            <TableHead>
              <TableRow>
                <StyledHeaderCell sx={stickyLeftHeader}>Name</StyledHeaderCell>
                <StyledHeaderCell>Contact</StyledHeaderCell>
                <StyledHeaderCell>Status</StyledHeaderCell>
                <StyledHeaderCell>Joined</StyledHeaderCell>
                <StyledHeaderCell>Jobs</StyledHeaderCell>
                <StyledHeaderCell>Accept rate</StyledHeaderCell>
                <StyledHeaderCell>Missed</StyledHeaderCell>
                <StyledHeaderCell>Dispatch→accept</StyledHeaderCell>
                <StyledHeaderCell>Pickup dist</StyledHeaderCell>
                <StyledHeaderCell>Park search</StyledHeaderCell>
                <StyledHeaderCell>Retrieval</StyledHeaderCell>
                <StyledHeaderCell>Idle time</StyledHeaderCell>
                <StyledHeaderCell>ASP/hr</StyledHeaderCell>
                <StyledHeaderCell align="right" sx={stickyRightHeader}>Action</StyledHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((v, idx) => {
                const id = rowId(v);
                const active = isActive(v);
                const busy = !!pending[id];
                const m = v.metrics || {};
                return (
                  <TableRow key={id || idx} sx={{ '&:hover td': { backgroundColor: '#f4f3ef' }, '&:last-child td': { borderBottom: 'none' } }}>
                    <StyledCell sx={{ fontWeight: 500, ...stickyLeftCell }}>{fullName(v) || '—'}</StyledCell>
                    <StyledCell sx={{ color: '#6b6b68' }}>{v.phone || v.phoneNumber || v.email || '—'}</StyledCell>
                    <StyledCell>
                      <StatusPill active={active}>{active ? 'Active' : 'Inactive'}</StatusPill>
                    </StyledCell>
                    <StyledCell sx={{ fontFamily: '"SF Mono", "Fira Code", monospace', color: '#6b6b68' }}>
                      {formatDate(v.createdAt || v.joinedAt)}
                    </StyledCell>
                    <MonoCell>{m.jobsCompleted ?? '—'}</MonoCell>
                    <MonoCell sx={{ color: m.acceptanceRate != null ? getAcceptColor(m.acceptanceRate) : 'inherit' }}>
                      {m.acceptanceRate ?? '—'}
                    </MonoCell>
                    <MonoCell>{m.missedRequests ?? '—'}</MonoCell>
                    <MonoCell>{m.dispatchAcceptTime ?? '—'}</MonoCell>
                    <MonoCell>{m.pickupDistance ?? '—'}</MonoCell>
                    <MonoCell>{m.parkingSearchTime ?? '—'}</MonoCell>
                    <MonoCell>{m.retrievalTime ?? '—'}</MonoCell>
                    <MonoCell>{m.idleTime ?? '—'}</MonoCell>
                    <MonoCell>{m.aspMovesPerHour ?? '—'}</MonoCell>
                    <StyledCell align="right" sx={stickyRightCell}>
                      <Button
                        size="small"
                        disabled={busy || !id}
                        onClick={() => handleActionClick(v)}
                        sx={{
                          textTransform: 'none',
                          fontSize: '11px',
                          color: active ? '#993C1D' : '#1D9E75',
                          border: `0.5px solid ${active ? '#993C1D' : '#1D9E75'}`,
                          padding: '2px 10px',
                          minWidth: 86,
                          '&.Mui-disabled': {
                            color: active ? 'rgba(153, 60, 29, 0.5)' : 'rgba(29, 158, 117, 0.5)',
                            borderColor: active ? 'rgba(153, 60, 29, 0.3)' : 'rgba(29, 158, 117, 0.3)',
                          },
                        }}
                      >
                        {busy ? (
                          <CircularProgress
                            size={12}
                            thickness={5}
                            sx={{ color: active ? '#993C1D' : '#1D9E75' }}
                          />
                        ) : active ? 'Deactivate' : 'Reactivate'}
                      </Button>
                    </StyledCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <StyledCell colSpan={14} sx={{ textAlign: 'center', color: '#9b9b97' }}>
                    {rows.length === 0 ? 'No valets found.' : 'No results match your search.'}
                  </StyledCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </TableWrapper>

      <Dialog
        open={!!confirmTarget}
        onClose={() => setConfirmTarget(null)}
        PaperProps={{ sx: { borderRadius: '12px', padding: '4px' } }}
      >
        <DialogTitle sx={{ fontSize: '14px', fontWeight: 600 }}>
          {confirmTarget?.action === 'reactivate' ? 'Reactivate' : 'Deactivate'}{' '}
          {confirmTarget ? fullName(confirmTarget.valet) || 'this valet' : 'this valet'}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '13px', color: '#6b6b68' }}>
            {confirmTarget?.action === 'reactivate'
              ? 'This valet will start receiving job requests again.'
              : 'This valet will stop receiving new job requests.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '8px 16px 12px' }}>
          <Button
            onClick={() => setConfirmTarget(null)}
            sx={{ textTransform: 'none', fontSize: '12px', color: '#6b6b68' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              backgroundColor: confirmTarget?.action === 'reactivate' ? '#1D9E75' : '#993C1D',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: confirmTarget?.action === 'reactivate' ? '#167a5a' : '#7a2f16',
              },
              borderRadius: '8px',
              padding: '4px 14px',
            }}
            variant="contained"
            disableElevation
          >
            {confirmTarget?.action === 'reactivate' ? 'Reactivate' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function ValetsTab({ data, rosterData, rosterError }) {
  if (!data) return null;
  const d = data;

  const fleet = d.fleetAverages || d;
  const valets = mergeValetData(d);
  const maxCompleted = valets.length > 0 ? Math.max(...valets.map(v => v.completed || 0)) : 1;

  return (
    <Box>
      <SectionHead>Fleet averages</SectionHead>
      <Grid4>
        <KpiCard label="Avg jobs per valet" value={fleet.avgJobsPerValet || '—'} subtitle="this period" />
        <KpiCard label="Avg acceptance rate" value={fleet.avgAcceptanceRate || '—'} subtitle="target > 92%" />
        <KpiCard label="Avg missed rate" value={fleet.avgMissedRate || '—'} subtitle="target < 8%" />
        <KpiCard label="Avg dispatch-accept" value={fleet.avgDispatchAccept || '—'} subtitle="response latency" />
      </Grid4>

      {valets.length > 0 && (
        <>
          <SectionHead sx={{ marginTop: '4px' }}>Per-valet breakdown</SectionHead>
          <TableWrapper>
            <Box sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 860 }}>
                <TableHead>
                  <TableRow>
                    <StyledHeaderCell>Valet</StyledHeaderCell>
                    <StyledHeaderCell>Jobs completed</StyledHeaderCell>
                    <StyledHeaderCell>Jobs/hr</StyledHeaderCell>
                    <StyledHeaderCell>Dispatch→accept</StyledHeaderCell>
                    <StyledHeaderCell>Accept rate</StyledHeaderCell>
                    <StyledHeaderCell>Missed rate</StyledHeaderCell>
                    <StyledHeaderCell>Pickup dist</StyledHeaderCell>
                    <StyledHeaderCell>Park search</StyledHeaderCell>
                    <StyledHeaderCell>Retrieval</StyledHeaderCell>
                    <StyledHeaderCell>Idle time</StyledHeaderCell>
                    <StyledHeaderCell>ASP/hr</StyledHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {valets.map((v, idx) => (
                    <TableRow key={v.valetId || idx} sx={{ '&:hover td': { backgroundColor: '#f4f3ef' }, '&:last-child td': { borderBottom: 'none' } }}>
                      <StyledCell sx={{ fontWeight: 500 }}>{v.valetName || v.name || '—'}</StyledCell>
                      <StyledCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Box sx={{ flex: 1, height: 5, backgroundColor: '#eceae4', borderRadius: '3px', overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', width: `${Math.round((v.completed || 0) / maxCompleted * 100)}%`, backgroundColor: '#6b6b68', borderRadius: '3px' }} />
                          </Box>
                          <Typography sx={{ fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: '12px', minWidth: '36px', textAlign: 'right' }}>
                            {v.completed ?? '—'}
                          </Typography>
                        </Box>
                      </StyledCell>
                      <MonoCell>{v.jobsPerHour ?? '—'}</MonoCell>
                      <MonoCell>{v.dispatchAccept != null ? `${v.dispatchAccept}s` : '—'}</MonoCell>
                      <MonoCell sx={{ color: v.acceptRate != null ? getAcceptColor(v.acceptRate) : 'inherit' }}>
                        {v.acceptRate != null ? `${v.acceptRate}%` : '—'}
                      </MonoCell>
                      <MonoCell sx={{ color: v.missedRate != null ? getMissedColor(v.missedRate) : 'inherit' }}>
                        {v.missedRate != null ? `${v.missedRate}%` : '—'}
                      </MonoCell>
                      <MonoCell>{v.pickupDist != null ? `${v.pickupDist} mi` : '—'}</MonoCell>
                      <MonoCell>{v.parkSearch != null ? `${v.parkSearch} min` : '—'}</MonoCell>
                      <MonoCell>{v.retrieval != null ? `${v.retrieval} min` : '—'}</MonoCell>
                      <MonoCell>{v.idleTime != null ? `${v.idleTime} min` : '—'}</MonoCell>
                      <MonoCell>{v.aspPerHour ?? '—'}</MonoCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </TableWrapper>
        </>
      )}

      <Divider />

      <RosterSection rosterData={rosterData} rosterError={rosterError} />
    </Box>
  );
}
